import { Link, useNavigate } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { useDashboard, useTrialStatus } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { TrialBanner } from '../../components/TrialBanner';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatNumber, formatTime } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ProgressBar,
  ProgressRing,
  SkeletonCard,
  StatTile,
  WorkoutCard,
} from '../../ui';

/**
 * Početni ekran klijenta nad `GET /v1/me/dashboard`.
 *
 * Prikazuje točno ono što ruta vraća. Ranije je čitao izmišljena polja
 * (`consistency`, `kcalGoal`, `weekVolumeKg`, `lastMessage.authorName`) koja
 * postoje samo u mocku — ekran je radio u demou, a nad pravim API-jem bi ostao
 * prazan.
 */
export function Dashboard() {
  const { t, locale } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const query = useDashboard();
  const trial = useTrialStatus();

  return (
    <>
      <PageHeader
        eyebrow={t('dash.today')}
        title={user?.firstName ? t('dash.greeting', { name: user.firstName }) : t('dash.greetingAnon')}
      />

      <TrialBanner status={trial.data} className="mb-5" />

      <QueryBoundary
        query={query}
        skeleton={
          <div className="grid gap-4 md:grid-cols-2">
            <SkeletonCard className="md:col-span-2" />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        }
      >
        {(data) => (
          <div className="grid gap-4 md:grid-cols-2">
            <section className="md:col-span-2">
              <p className="eyebrow mb-3">{t('portal.todayWorkout')}</p>
              {data.todayWorkout ? (
                <WorkoutCard
                  title={data.todayWorkout.name}
                  to={`/trening/${data.todayWorkout.workoutId}`}
                  exercisesLabel={t('dash.exercisesCount', { n: data.todayWorkout.exerciseCount })}
                  {...(data.todayWorkout.estimatedMinutes !== null
                    ? { durationLabel: t('dash.minutes', { n: data.todayWorkout.estimatedMinutes }) }
                    : {})}
                  statusLabel={t('portal.startWorkout')}
                />
              ) : (
                <EmptyState
                  message={t('empty.noWorkoutToday')}
                  actionLabel={t('portal.training')}
                  onAction={() => void navigate('/trening')}
                />
              )}
            </section>

            <Card className="flex items-center gap-5">
              <ProgressRing
                value={data.week.completed}
                max={data.week.prescribed}
                ariaLabel={t('a11y.progressRing', {
                  value: data.week.completed,
                  max: data.week.prescribed,
                })}
              />
              <div>
                <p className="eyebrow mb-1">{t('dash.consistency')}</p>
                <p className="font-display text-[21px] font-semibold text-ink">
                  {t('dash.consistencyOf', {
                    done: data.week.completed,
                    planned: data.week.prescribed,
                  })}
                </p>
                <p className="mt-1 text-[13px] text-ink-muted">{t('dash.consistencyHelp')}</p>
              </div>
            </Card>

            <Card>
              <CardHeader
                title={t('dash.caloriesToday')}
                action={
                  <Link to="/prehrana" className="text-[13px] text-accent-dark underline">
                    {t('portal.nutrition')}
                  </Link>
                }
              />
              {/*
                Cilj kalorija još nije dio modela podataka — API ga vraća kao null.
                Dok ga nema, prikazuje se sam unos umjesto izmišljene ciljne vrijednosti.
              */}
              {data.nutrition.targetKcal !== null ? (
                <ProgressBar
                  value={data.nutrition.kcal}
                  max={data.nutrition.targetKcal}
                  valueLabel={t('dash.kcalOf', {
                    eaten: formatNumber(data.nutrition.kcal, locale),
                    goal: formatNumber(data.nutrition.targetKcal, locale),
                  })}
                  label={t('nutrition.kcal')}
                />
              ) : (
                <p className="font-display text-[32px] font-semibold leading-none text-ink">
                  {formatNumber(data.nutrition.kcal, locale)}
                  <span className="ml-1 font-body text-[13px] text-ink-muted">kcal</span>
                </p>
              )}
              <div className="mt-5 grid grid-cols-2 gap-3">
                <StatTile
                  label={t('nutrition.water')}
                  value={formatNumber(data.nutrition.waterMl / 1000, locale, 1)}
                  unit="l"
                />
                <StatTile
                  label={t('dash.weight')}
                  value={
                    data.weight.current !== null ? formatNumber(data.weight.current, locale, 1) : '—'
                  }
                  unit="kg"
                  {...(data.weight.goal !== null
                    ? {
                        delta: {
                          value: t('dash.weightGoal', {
                            n: formatNumber(data.weight.goal, locale, 1),
                          }),
                          direction: 'flat' as const,
                        },
                      }
                    : {})}
                />
              </div>
            </Card>

            <StatTile
              label={t('habits.streak')}
              value={data.streakDays}
              unit={t('dash.streakUnit')}
            />

            <Card>
              <CardHeader
                title={t('dash.lastMessage')}
                {...(data.unreadMessages > 0
                  ? { action: <Badge tone="accent">{data.unreadMessages}</Badge> }
                  : {})}
              />
              {data.lastTrainerMessage ? (
                <div className="flex flex-col gap-3">
                  <p className="text-[14.5px] leading-relaxed text-ink">
                    {data.lastTrainerMessage.body}
                  </p>
                  <p className="text-[12px] text-ink-muted">
                    {formatTime(data.lastTrainerMessage.at, locale)}
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="self-start"
                    onClick={() => void navigate('/poruke')}
                  >
                    {t('dash.openMessages')}
                  </Button>
                </div>
              ) : (
                <EmptyState
                  message={t('empty.noMessages')}
                  actionLabel={t('dash.openMessages')}
                  onAction={() => void navigate('/poruke')}
                />
              )}
            </Card>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
